/**
 * 调用后端 MCP 服务（与 iOS MCPClient 一致，docs/MCP.md）：同源 POST /mcp（JSON-RPC），
 * Worker 用会话 Cookie 补上 Authorization。
 * 不用 core 的 request()：/mcp 的 401 不一定是登录过期（例如令牌的 audience 不对），不能因此退出登录；
 * 这里 401 时刷新一次令牌再重试，仍然 401 就作为错误显示在对话里。
 */
import { CSRF_HEADER, refreshSession } from "../../core";

export interface ToolInfo {
  name: string;
  title: string;
  description: string;
  inputSchema: unknown;
  readOnly: boolean;
  destructive: boolean;
}

export interface CallResult {
  text: string;
  isError: boolean;
}

interface RpcResponse {
  result?: Record<string, unknown>;
  error?: { message?: string };
}

export class McpClient {
  private initialized = false;
  private nextId = 0;
  private tools: ToolInfo[] = [];

  async loadTools(signal?: AbortSignal): Promise<ToolInfo[]> {
    if (this.tools.length) return this.tools;
    if (!this.initialized) {
      await this.rpc(
        "initialize",
        { protocolVersion: "2025-06-18", capabilities: {}, clientInfo: { name: "kids-planner-parent-web", version: "0.1.0" } },
        signal,
      );
      this.initialized = true;
    }
    const result = await this.rpc("tools/list", {}, signal);
    const list = Array.isArray(result.tools) ? (result.tools as Record<string, unknown>[]) : [];
    this.tools = list
      .filter((t) => typeof t.name === "string")
      .map((t) => {
        const ann = (t.annotations ?? {}) as Record<string, unknown>;
        return {
          name: t.name as string,
          title: (t.title as string) ?? (ann.title as string) ?? (t.name as string),
          description: (t.description as string) ?? "",
          inputSchema: t.inputSchema ?? { type: "object" },
          readOnly: ann.readOnlyHint === true,
          destructive: ann.destructiveHint === true,
        };
      });
    return this.tools;
  }

  async callTool(name: string, args: Record<string, unknown>, signal?: AbortSignal): Promise<CallResult> {
    const result = await this.rpc("tools/call", { name, arguments: args }, signal);
    const content = Array.isArray(result.content) ? (result.content as { text?: unknown }[]) : [];
    const text = content
      .map((c) => (typeof c.text === "string" ? c.text : null))
      .filter((s): s is string => s != null)
      .join("\n");
    return { text, isError: result.isError === true };
  }

  private async rpc(method: string, params: Record<string, unknown>, signal?: AbortSignal): Promise<Record<string, unknown>> {
    this.nextId += 1;
    const body = JSON.stringify({ jsonrpc: "2.0", id: this.nextId, method, params });
    const post = async () => {
      try {
        return await fetch("/mcp", {
          method: "POST",
          headers: { [CSRF_HEADER]: "1", "Content-Type": "application/json", Accept: "application/json, text/event-stream" },
          body,
          signal,
        });
      } catch (e) {
        if ((e as Error)?.name === "AbortError") throw e;
        throw new Error("连不上家庭数据服务，请检查网络");
      }
    };
    let res = await post();
    if (res.status === 401 && (await refreshSession().catch(() => "failed")) !== "failed") res = await post();
    const text = await res.text();
    let obj: RpcResponse & { error_description?: string };
    try {
      obj = JSON.parse(text) as RpcResponse & { error_description?: string };
    } catch {
      throw new Error(`家庭数据服务返回异常（${res.status}）`);
    }
    if (res.status === 401) throw new Error(`家庭数据服务拒绝了登录凭证（${obj.error_description ?? "401"}）`);
    if (obj.error) throw new Error(typeof obj.error === "object" ? (obj.error.message ?? "家庭数据服务出错") : `家庭数据服务出错（${res.status}）`);
    if (!res.ok || !obj.result) throw new Error(`家庭数据服务出错（${res.status}）`);
    return obj.result;
  }
}
